import React, { ReactElement } from 'react';
import { StyleSheet } from 'react-native';

import ArrowIcon from '@/assets/icons/arrow';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { AppColors, getThemeColorHexValue } from '@/components/lib/theme';
import { common, designSystem } from '@/styles/styles';
import { Outcome } from '@/types/api.generated';

type PickButtonProps = {
    outcome: string;
    isSelected: boolean;
    onPress: () => void;
    backgroundColor?: AppColors;
    testID?: string;
};

/**
 * PickProjectionButton renders the More/Less button used for picking a player's projection
 */
export const PickProjectionRoundButton = ({
    outcome,
    isSelected,
    onPress,
    backgroundColor = 'neutral700',
    testID,
}: PickButtonProps) => {
    const arrowStyle = outcome === Outcome.More ? {} : common.rotate180;

    return (
        <RoundButton
            onPress={onPress}
            label={
                <ArrowIcon
                    style={arrowStyle}
                    color={isSelected ? designSystem.colors.neutral900 : ds.colorValue.icon.primary}
                />
            }
            backgroundColor={isSelected ? ds.colorToken.background.neutral.inverse : backgroundColor}
            active={isSelected}
            analyticsTag={outcome}
            testID={testID}
        />
    );
};

/**
 * Function that renders the More/Less button used for picking a player's projection for projection tiles.
 * @param {PickButtonProps} params
 * @returns {JSX.Element}
 */
export const PickProjectionSquareButton = ({ outcome, isSelected, onPress, testID }: PickButtonProps) => {
    const color = isSelected ? designSystem.colors.neutral900 : ds.colorValue.text.primary;

    return (
        <Button
            active={isSelected}
            onPress={onPress}
            size="s"
            shape="rect"
            label={
                <Text variant="titleSmall" style={{ color }} textTransform="capitalize">
                    {outcome}
                </Text>
            }
            style={styles.squareButton}
            analyticsTag={outcome}
            testID={`projectionButton-${outcome}-${isSelected ? 'SELECTED' : 'NOT_SELECTED'}-${testID}`}
        />
    );
};

export const RoundButton = ({
    label,
    onPress,
    backgroundColor,
    active,
    analyticsTag,
    testID,
}: {
    label: string | ReactElement;
    backgroundColor: AppColors;
    active?: boolean;
    onPress: () => void;
    analyticsTag?: string;
    testID?: string;
}) => (
    <Button
        active={active}
        onPress={onPress}
        label={label}
        size="m"
        shape={'circle'}
        style={{ backgroundColor: getThemeColorHexValue(backgroundColor), ...styles.roundButton }}
        analyticsTag={`${analyticsTag}-pickButton`}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        testID={`projectionButton-${analyticsTag}-${testID}`}
    />
);

const styles = StyleSheet.create({
    squareButton: {
        paddingHorizontal: 12,
    },
    roundButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
    },
});
